import React, { useRef } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { X, FileEarmarkPdf, Printer } from 'react-bootstrap-icons';
import moment from 'moment';
import 'moment/locale/es';
import { TipDistributionConfig } from '../../types';
import { formatCurrency } from '../../tipCalculationsNew';
import './TipDistributionPDFModal.scss';

// MedioPunto orange color (same as SessionBriefModal)
const MEDIOPUNTO_ORANGE = '#FC7556';

// Logo path (same as SessionBriefModal)
const MEDIOPUNTO_LOGO_PATH = '/logos/Recurso 53MEDIOPUNTO.png';

interface TipDistributionPDFModalProps {
  show: boolean;
  onHide: () => void;
  config: TipDistributionConfig | null;
}

interface UserTotal {
  name: string;
  rate: number;
  total: number;
  isGroup: boolean;
  users?: { name: string; rate: number; total: number }[];
}

const TipDistributionPDFModal: React.FC<TipDistributionPDFModalProps> = ({
  show,
  onHide,
  config,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isPrinting, setIsPrinting] = React.useState(false);

  // Configure moment locale
  React.useEffect(() => {
    moment.locale('es');
  }, []);

  if (!config) return null;

  // Calculate user totals
  const calculateUserTotals = (): UserTotal[] => {
    const userTotals: UserTotal[] = [];

    config.groups.forEach(group => {
      if (group.isRealGroup && group.users.length > 0) {
        let groupTotal = 0;
        const groupUsers = group.users.map(user => {
          let userTotal = 0;
          config.days.forEach(day => {
            const isAttending = user.attendance[day.date] ?? true;
            if (isAttending) {
              const dayTotal = day.declaredTotal;
              const totalRate = config.groups.reduce((sum, g) => {
                const groupAttending = g.attendance[day.date] ?? true;
                return groupAttending ? sum + g.rate : sum;
              }, 0);

              if (totalRate > 0) {
                const groupShare = (dayTotal * group.rate) / totalRate;
                const usersAttending = group.users.filter(u => u.attendance[day.date] ?? true);
                const userRateTotal = usersAttending.reduce((s, u) => s + u.rate, 0);
                if (userRateTotal > 0) {
                  userTotal += (groupShare * user.rate) / userRateTotal;
                }
              }
            }
          });
          groupTotal += userTotal;
          return { name: user.name, rate: user.rate, total: userTotal };
        });

        userTotals.push({
          name: group.name,
          rate: group.rate,
          total: groupTotal,
          isGroup: true,
          users: groupUsers,
        });
      } else {
        let userTotal = 0;
        config.days.forEach(day => {
          const isAttending = group.attendance[day.date] ?? true;
          if (isAttending) {
            const dayTotal = day.declaredTotal;
            const totalRate = config.groups.reduce((sum, g) => {
              const groupAttending = g.attendance[day.date] ?? true;
              return groupAttending ? sum + g.rate : sum;
            }, 0);

            if (totalRate > 0) {
              userTotal += (dayTotal * group.rate) / totalRate;
            }
          }
        });

        userTotals.push({
          name: group.name,
          rate: group.rate,
          total: userTotal,
          isGroup: false,
        });
      }
    });

    return userTotals;
  };

  const userTotals = calculateUserTotals();

  // Get local name
  const getLocalName = (): string => {
    try {
      const userContext = localStorage.getItem('UserContext');
      if (userContext) {
        const parsed = JSON.parse(userContext);
        return parsed.local_name || 'MedioPunto';
      }
    } catch (e) {
      console.error('Error getting local name:', e);
    }
    return 'MedioPunto';
  };

  // Handle print
  const handlePrint = () => {
    if (!contentRef.current) return;

    setIsPrinting(true);

    const printContent = contentRef.current.innerHTML;
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      setIsPrinting(false);
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Distribución de Propinas - ${moment(config.startDate).format('DD-MM-YYYY')}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Courier New', monospace; 
              padding: 20px; 
              background: white;
              color: #2d3748;
            }
            .pdf-content { max-width: 600px; margin: 0 auto; }
            .pdf-logo { text-align: center; margin-bottom: 1.5rem; }
            .pdf-logo img { max-width: 150px; height: auto; }
            .pdf-header { text-align: center; margin-bottom: 1.5rem; }
            .pdf-local-name { font-size: 1.4rem; font-weight: 700; margin: 0 0 0.5rem 0; text-transform: uppercase; letter-spacing: 1px; }
            .pdf-title { font-size: 1.1rem; font-weight: 600; margin: 0 0 0.5rem 0; color: ${MEDIOPUNTO_ORANGE}; }
            .pdf-period { font-size: 0.85rem; color: #718096; }
            .pdf-divider { border-top: 2px dashed #e2e8f0; margin: 1.5rem 0; }
            .pdf-section { margin-bottom: 1.5rem; }
            .pdf-section-title { font-size: 0.95rem; font-weight: 700; margin: 0 0 0.75rem 0; text-align: center; text-transform: uppercase; letter-spacing: 0.5px; }
            .pdf-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
            .pdf-table th, .pdf-table td { padding: 0.5rem; text-align: center; border-bottom: 1px solid #e2e8f0; }
            .pdf-table th { background: #f7fafc; font-weight: 700; color: #718096; }
            .pdf-table .highlight { color: ${MEDIOPUNTO_ORANGE}; font-weight: 700; }
            .pdf-row { display: flex; justify-content: space-between; padding: 0.4rem 0; font-size: 0.9rem; }
            .pdf-row .label { font-weight: 500; }
            .pdf-row .value { font-weight: 600; }
            .pdf-user-card { background: #f7fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 0.75rem 1rem; margin-bottom: 0.5rem; }
            .pdf-user-header { display: flex; justify-content: space-between; align-items: center; }
            .pdf-user-name { font-weight: 700; font-size: 0.95rem; }
            .pdf-user-rate { font-size: 0.75rem; padding: 0.2rem 0.5rem; border-radius: 10px; background: rgba(252, 117, 86, 0.15); color: ${MEDIOPUNTO_ORANGE}; font-weight: 600; }
            .pdf-user-total { font-size: 1rem; font-weight: 700; color: ${MEDIOPUNTO_ORANGE}; text-align: right; margin-top: 0.25rem; }
            .pdf-group-card { border: 2px solid ${MEDIOPUNTO_ORANGE}; border-radius: 8px; padding: 1rem; margin-bottom: 0.75rem; }
            .pdf-group-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem; padding-bottom: 0.5rem; border-bottom: 1px dashed #e2e8f0; }
            .pdf-group-name { font-weight: 700; color: ${MEDIOPUNTO_ORANGE}; font-size: 1rem; }
            .pdf-group-total { font-weight: 700; color: ${MEDIOPUNTO_ORANGE}; font-size: 1rem; }
            .pdf-group-users { padding-left: 0.5rem; }
            .pdf-group-users .pdf-user-card { margin-bottom: 0.35rem; padding: 0.5rem 0.75rem; }
            .pdf-grand-total { background: ${MEDIOPUNTO_ORANGE}; color: white; padding: 0.75rem 1rem; border-radius: 8px; display: flex; justify-content: space-between; font-weight: 700; font-size: 1.1rem; margin-top: 1rem; }
            .pdf-signature { margin-top: 2rem; text-align: center; }
            .pdf-signature-line { border-bottom: 1px solid #2d3748; width: 200px; margin: 0 auto 0.5rem auto; height: 40px; }
            .pdf-signature-label { font-size: 0.8rem; color: #718096; }
            .pdf-footer { margin-top: 2rem; padding-top: 1rem; border-top: 1px solid #e2e8f0; font-size: 0.8rem; color: #718096; text-align: center; }
            @media print { body { padding: 1rem; } }
            @page { margin: 1cm; }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);

    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
      setIsPrinting(false);
    }, 300);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="tip-distribution-pdf-modal"
    >
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">
            Vista Previa - Distribución de Propinas
          </Modal.Title>
        </div>
        <Button variant="none" onClick={onHide} className="modal-close-btn">
          <X size={24} />
        </Button>
      </Modal.Header>

      <Modal.Body>
        <div className="pdf-preview-container">
          <div className="pdf-content" ref={contentRef}>
            {/* Logo */}
            <div className="pdf-logo">
              <img src={MEDIOPUNTO_LOGO_PATH} alt="MedioPunto" />
            </div>

            {/* Header */}
            <div className="pdf-header">
              <h1 className="pdf-local-name">{getLocalName()}</h1>
              <h2 className="pdf-title">Distribución de Propinas</h2>
              <p className="pdf-period">
                {moment(config.startDate).format('DD [de] MMMM')} - {moment(config.endDate).format('DD [de] MMMM, YYYY')}
              </p>
            </div>

            <div className="pdf-divider" />

            {/* Days Summary */}
            <div className="pdf-section">
              <h3 className="pdf-section-title">Resumen por Día</h3>
              <table className="pdf-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Declarado</th>
                    <th>Distribuido</th>
                  </tr>
                </thead>
                <tbody>
                  {[...config.days].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(day => (
                    <tr key={day.date}>
                      <td>{moment(day.date).format('ddd DD/MM')}</td>
                      <td>{formatCurrency(day.declaredTotal)}</td>
                      <td className="highlight">{formatCurrency(day.distributedTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pdf-divider" />

            {/* Totals */}
            <div className="pdf-section">
              <h3 className="pdf-section-title">Totales</h3>
              <div className="pdf-row">
                <span className="label">Total Declarado:</span>
                <span className="value">{formatCurrency(config.totals.declared)}</span>
              </div>
              <div className="pdf-row">
                <span className="label">Total Distribuido:</span>
                <span className="value">{formatCurrency(config.totals.distributed)}</span>
              </div>
              {config.totals.difference !== 0 && (
                <div className="pdf-row">
                  <span className="label">Diferencia:</span>
                  <span className="value">
                    {config.totals.difference > 0 ? '+' : ''}{formatCurrency(config.totals.difference)}
                  </span>
                </div>
              )}
            </div>

            <div className="pdf-divider" />

            {/* User Distribution */}
            <div className="pdf-section">
              <h3 className="pdf-section-title">Distribución por Persona</h3>
              
              {userTotals.map((item, index) => {
                if (item.isGroup && item.users) {
                  return (
                    <div key={index} className="pdf-group-card">
                      <div className="pdf-group-header">
                        <span className="pdf-group-name">{item.name}</span>
                        <span className="pdf-group-total">{formatCurrency(item.total)}</span>
                      </div>
                      <div className="pdf-group-users">
                        {item.users.map((user, userIndex) => (
                          <div key={userIndex} className="pdf-user-card">
                            <div className="pdf-user-header">
                              <span className="pdf-user-name">{user.name}</span>
                              <span className="pdf-user-rate">{user.rate}%</span>
                            </div>
                            <div className="pdf-user-total">{formatCurrency(user.total)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div key={index} className="pdf-user-card">
                      <div className="pdf-user-header">
                        <span className="pdf-user-name">{item.name}</span>
                        <span className="pdf-user-rate">{item.rate}%</span>
                      </div>
                      <div className="pdf-user-total">{formatCurrency(item.total)}</div>
                    </div>
                  );
                }
              })}

              <div className="pdf-grand-total">
                <span>TOTAL A DISTRIBUIR:</span>
                <span>{formatCurrency(config.totals.distributed)}</span>
              </div>
            </div>

            <div className="pdf-divider" />

            {/* Signature */}
            <div className="pdf-signature">
              <div className="pdf-signature-line" />
              <p className="pdf-signature-label">Firma de Conformidad</p>
            </div>

            {/* Footer */}
            <div className="pdf-footer">
              <p>Generado el {moment().format('DD/MM/YYYY [a las] HH:mm')}</p>
              <p>MedioPunto - Sistema de Gestión</p>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button
            variant="none"
            className="form-button-cancel"
            onClick={onHide}
          >
            Cerrar
          </Button>
          <Button
            variant="none"
            className="form-button-confirm"
            onClick={handlePrint}
            disabled={isPrinting}
          >
            {isPrinting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Imprimiendo...
              </>
            ) : (
              <>
                <Printer className="me-2" />
                Imprimir / Guardar PDF
              </>
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default TipDistributionPDFModal;
